/**
 * Receiver icon placement (GDD 6.4 and 6.10): icons never overlap, stay inside the safe area,
 * and never sit in the bottom-right thumb zone. Pure, so it is unit tested.
 */
export interface IconAnchor {
  id: number;
  /** Screen point above the receiver's head, in CSS px. */
  x: number;
  y: number;
}

export interface PlacedIcon {
  id: number;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
  /** True when the icon was pushed far enough from its anchor to need a connector line. */
  moved: boolean;
}

export interface IconLayoutOptions {
  size: number;
  gap: number;
  width: number;
  height: number;
  edgeFraction: number;
  bottomFraction: number;
  thumbWidthFraction: number;
  thumbHeightFraction: number;
}

const MOVED_PX = 6;
const PASSES = 8;

export function layoutIcons(anchors: readonly IconAnchor[], o: IconLayoutOptions): PlacedIcon[] {
  const half = o.size / 2;
  const minX = o.width * o.edgeFraction + half;
  const maxX = o.width * (1 - o.edgeFraction) - half;
  const minY = o.height * o.edgeFraction + half;
  const maxY = o.height * (1 - o.bottomFraction) - half;
  const thumbLeft = o.width * (1 - o.thumbWidthFraction);
  const thumbTop = o.height * (1 - o.thumbHeightFraction);
  const span = o.size + o.gap;

  const clampOne = (p: PlacedIcon) => {
    p.x = Math.min(maxX, Math.max(minX, p.x));
    p.y = Math.min(maxY, Math.max(minY, p.y));
    if (p.x + half > thumbLeft && p.y + half > thumbTop) p.y = thumbTop - half;
  };

  const placed = anchors.map((a) => ({ id: a.id, x: a.x, y: a.y, anchorX: a.x, anchorY: a.y, moved: false }));
  placed.forEach(clampOne);
  placed.sort((a, b) => a.x - b.x || a.id - b.id);

  for (let pass = 0; pass < PASSES; pass++) {
    let changed = false;
    for (let i = 0; i < placed.length; i++) {
      for (let j = i + 1; j < placed.length; j++) {
        const a = placed[i]!;
        const b = placed[j]!;
        const dx = b.x - a.x;
        if (Math.abs(dx) >= span || Math.abs(b.y - a.y) >= span) continue;
        const push = (span - Math.abs(dx)) / 2;
        a.x -= push;
        b.x += push;
        changed = true;
      }
    }
    placed.forEach(clampOne);
    placed.sort((a, b) => a.x - b.x || a.id - b.id);
    if (!changed) break;
  }

  // Edge clamping can squeeze icons back together: sweep right, then left, to guarantee spacing.
  for (let i = 1; i < placed.length; i++) {
    const a = placed[i - 1]!;
    const b = placed[i]!;
    if (Math.abs(b.y - a.y) < span && b.x - a.x < span) b.x = a.x + span;
  }
  for (let i = placed.length - 2; i >= 0; i--) {
    const a = placed[i]!;
    const b = placed[i + 1]!;
    if (b.x > maxX) b.x = maxX;
    if (Math.abs(b.y - a.y) < span && b.x - a.x < span) a.x = b.x - span;
  }
  // If the row is still too wide for the screen, stack the overflow on a second row.
  for (let i = 0; i < placed.length; i++) {
    const p = placed[i]!;
    if (p.x < minX) {
      p.x = minX + (i * span) % Math.max(span, maxX - minX);
      p.y = Math.min(maxY, p.y + span);
    }
  }

  for (const p of placed) p.moved = Math.hypot(p.x - p.anchorX, p.y - p.anchorY) > MOVED_PX;
  return placed;
}
